import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users, Copy, UserCheck, UserX, Trash2, RefreshCw,
  MessageCircle
} from 'lucide-react';

export default function Subscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get('/api/subscribers');
      setSubscribers(res.data);
    } catch (err) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  const toggleSubscriber = async (id) => {
    try {
      const res = await axios.patch(`/api/subscribers/${id}/toggle`);
      toast.success(res.data.is_active ? 'Subscriber activated' : 'Subscriber deactivated');
      fetchSubscribers();
    } catch (err) {
      toast.error('Operation failed');
    }
  };

  const copyId = async (chatId) => {
    await navigator.clipboard.writeText(String(chatId));
    toast.success('Chat ID copied!');
  };

  const deleteSubscriber = async () => {
    if (!deleteId) return;
    const idToDelete = deleteId;
    setDeleteId(null);
    try {
      await axios.delete(`/api/subscribers/${idToDelete}`);
      toast.success('Subscriber deleted');
      fetchSubscribers();
    } catch (err) {
      toast.error('Failed to delete subscriber');
    }
  };

  const activeCount = subscribers.filter(s => s.is_active).length;
  const inactiveCount = subscribers.filter(s => !s.is_active).length;

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
        {[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 animate-fadeInUp">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            Subscribers
          </h1>
          <p className="text-sm text-muted-foreground">Telegram bot subscribers</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSubscribers} className="gap-2">
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="stat-card bg-card/80 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
              {subscribers.length}
            </p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="stat-card bg-card/80 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold tabular-nums text-primary" style={{ fontFamily: 'Space Grotesk' }}>
              {activeCount}
            </p>
            <p className="text-xs text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card className="stat-card bg-card/80 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold tabular-nums text-destructive" style={{ fontFamily: 'Space Grotesk' }}>
              {inactiveCount}
            </p>
            <p className="text-xs text-muted-foreground">Inactive</p>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium">How to Subscribe?</p>
              <p className="text-xs text-muted-foreground mt-1">
                Users send <span className="font-mono text-foreground">/start</span> to
                {' '}<span className="font-mono text-primary">@kingtips2026_bot</span> on Telegram
                to automatically subscribe.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscribers List */}
      <div data-testid="subscribers-table" className="space-y-2">
        {subscribers.length === 0 ? (
          <Card className="bg-card/80 border-border/60">
            <CardContent className="py-12 text-center">
              <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No subscribers yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Users can subscribe by sending /start to the bot
              </p>
            </CardContent>
          </Card>
        ) : (
          subscribers.map((sub) => (
            <Card
              key={sub.id}
              data-testid="subscriber-row"
              className="bg-card/80 border-border/60 hover:border-border transition-colors duration-150"
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                      sub.is_active ? 'bg-primary/15' : 'bg-destructive/15'
                    }`}>
                      {sub.is_active ? (
                        <UserCheck className="w-4 h-4 text-primary" />
                      ) : (
                        <UserX className="w-4 h-4 text-destructive" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">
                          {sub.first_name || 'Unknown'}
                        </p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          sub.is_active ? 'badge-active' : 'badge-inactive'
                        }`}>
                          {sub.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {sub.username && <span>@{sub.username}</span>}
                        <span>&bull;</span>
                        <span className="font-mono">{sub.chat_id}</span>
                        {sub.created_at && (
                          <>
                            <span>&bull;</span>
                            <span>{new Date(sub.created_at).toLocaleDateString('en-US')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid="subscriber-id-copy-button"
                      onClick={() => copyId(sub.chat_id)}
                      className="h-8 w-8"
                      title="Copy Chat ID"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleSubscriber(sub.id)}
                      className="h-8 w-8"
                      title={sub.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {sub.is_active ? (
                        <UserX className="w-3.5 h-3.5 text-muted-foreground" />
                      ) : (
                        <UserCheck className="w-3.5 h-3.5 text-primary" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(sub.id)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subscriber</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this subscriber?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteSubscriber}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
